/*
 * Payroll computation.
 *
 *   Daily rate:            ₱700 (override per-employee via emp.dailyRate)
 *   Overtime:              hourly rate × 1.25 (+25% / hour)
 *   Special holiday:       daily rate × 1.30  (+30%  for the day worked)
 *   Regular holiday:       daily rate × 2.00  (+100% for the day worked)
 *   Weekend duty:          daily rate × 1.00  (treated as a regular work day)
 *   Deductions:            late, undertime (per minute), absent (per day)
 *
 * Inputs come from two existing sources the SalaryPage already fetches:
 *   - GET /api/timesheets (workedHours, overtimeHours, lateMinutes,
 *     undertimeMinutes, status, date)
 *   - GET /api/employees/{code}/calendar + /api/calendar/events
 *     (passed in as the merged calendarDays list, used for holidays)
 *
 * A day's type is resolved by looking up its calendar event label —
 * "Regular Holiday" → regularHoliday, "Special … Holiday" (or
 * "Special Non-Working") → specialHoliday, Sat/Sun → weekend, else
 * weekday. If a timesheet doesn't exist for a day we skip it silently
 * so the math stays stable even when a month isn't fully logged yet.
 */

export const DEFAULT_DAILY_RATE = 700;
export const HOURS_PER_DAY      = 8;
// Standard working days per month used to derive the daily rate from
// an employee's base (monthly) salary. 22 ≈ Mon-Fri weekdays in a
// typical month, which is the usual Philippine payroll convention.
export const WORK_DAYS_PER_MONTH = 22;

/** Derive a daily rate from a monthly base salary. Returns the default if salary is missing/invalid. */
export function dailyRateFromSalary(monthlySalary) {
  const n = Number(monthlySalary);
  if (!n || n <= 0) return DEFAULT_DAILY_RATE;
  return n / WORK_DAYS_PER_MONTH;
}

const OT_MULTIPLIER              = 1.25;  // +25% / hour
const SPECIAL_HOLIDAY_MULTIPLIER = 1.30;  // +30% / day
const REGULAR_HOLIDAY_MULTIPLIER = 2.00;  // +100% / day

/**
 * Classify a date into one of: regularHoliday | specialHoliday | weekend | weekday.
 *
 * Priority order:
 *   1. The raw calendar event type, if the day has any globalEvents.
 *      "Regular Holiday" → regularHoliday
 *      "Special Holiday" (or "Special Non-Working") → specialHoliday
 *      Plain "Holiday" → regularHoliday (Philippine default)
 *   2. Substring match on the event label/title — so even older events
 *      that predate the new event-type enum still classify correctly.
 *   3. Saturday/Sunday → weekend.
 *   4. Otherwise → weekday.
 */
export function classifyDay(date, calendarDay) {
  const events = Array.isArray(calendarDay?.globalEvents) ? calendarDay.globalEvents : [];

  // Primary signal: the structured event type set on the calendar event.
  for (const ev of events) {
    const t = String(ev?.eventType || "").toLowerCase();
    if (t === "regular holiday")                         return "regularHoliday";
    if (t === "special holiday" || t.startsWith("special non"))
                                                         return "specialHoliday";
    if (t === "holiday")                                 return "regularHoliday";
  }

  // Fallback: title/label substring match (back-compat with pre-typed data).
  const label = String(calendarDay?.eventLabel || "").toLowerCase();
  if (label.includes("regular holiday"))                 return "regularHoliday";
  if (label.includes("special holiday") || label.includes("special non"))
                                                         return "specialHoliday";
  if (label.includes("holiday"))                         return "regularHoliday";

  const dow = date.getDay();
  if (dow === 0 || dow === 6) return "weekend";
  return "weekday";
}

function isoDate(year, monthZeroBased, day) {
  return `${year}-${String(monthZeroBased + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Compute a full payroll breakdown for one employee/one month.
 *
 * @param {Array}  timesheets   Raw rows from /api/timesheets for the month.
 * @param {Array}  calendarDays Merged calendar rows (emp calendar + global
 *                              events), each with { day, eventLabel }.
 * @param {number} dailyRate    Peso daily rate. Defaults to ₱700.
 * @param {number} year         4-digit year.
 * @param {number} month        0-based month (matches JS Date.getMonth()).
 * @param {string} salaryType   "monthly" → fixed monthly base mode (full
 *                              salary regardless of clock-ins; only
 *                              unpaid absences deduct; holiday/OT extra
 *                              still applies as a PREMIUM on top of the
 *                              base). "daily" (or anything else, incl.
 *                              null/undefined) → existing pay-per-day
 *                              behaviour. Defaults to "daily" so older
 *                              callers keep their current behaviour.
 * @returns full breakdown incl. per-bucket pay, deductions, and netPay.
 */
export function computePayroll(timesheets, calendarDays, dailyRate, year, month, payrollDeductions, earnings, applyDeductionsOnDayOff, salaryType) {
  const rate        = Number(dailyRate) > 0 ? Number(dailyRate) : DEFAULT_DAILY_RATE;
  const hourlyRate  = rate / HOURS_PER_DAY;
  const minuteRate  = hourlyRate / 60;

  const calByDay = {};
  (calendarDays || []).forEach(d => { if (d?.day) calByDay[d.day] = d; });

  const byDate = {};
  (timesheets || []).forEach(ts => { if (ts?.date) byDate[ts.date] = ts; });

  let regularPay         = 0;
  let overtimePay        = 0;
  let specialHolidayPay  = 0;
  let regularHolidayPay  = 0;
  let lateDeduction      = 0;
  let undertimeDeduction = 0;
  let absentDeduction    = 0;
  let daysWorked         = 0;
  let daysAbsent         = 0;
  // Tracked separately so monthly-mode can rebuild holiday pay as a
  // PREMIUM ONLY (the base for that day is already inside the fixed
  // monthly salary). Counts only days the employee actually showed up
  // — paid leave on a holiday doesn't earn the premium because they
  // didn't work, but the base is still covered by the monthly salary.
  let regularHolidayWorkedDays = 0;
  let specialHolidayWorkedDays = 0;

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let d = 1; d <= daysInMonth; d++) {
    const ts = byDate[isoDate(year, month, d)];
    const cal = calByDay[d];

    // If no timesheet but there's a paid Leave calendar event, count it as paid leave
    if (!ts) {
      const hasLeaveEvent = cal?.globalEvents?.some(
        ev => (ev.eventType || "").toLowerCase() === "leave" && ev.affectsSalary
      );
      if (hasLeaveEvent) {
        daysWorked += 1;
        regularPay += rate;
      }
      continue;
    }

    if (ts.status === "Absent") {
      absentDeduction += rate;
      daysAbsent      += 1;
      continue;
    }

    const type       = classifyDay(new Date(year, month, d), cal);
    const workedHrs  = Number(ts.workedHours)      || 0;
    const otHrs      = Number(ts.overtimeHours)    || 0;
    const lateMin    = Number(ts.lateMinutes)      || 0;
    const underMin   = Number(ts.undertimeMinutes) || 0;

    // Base day pay — paid if the employee worked, is on a recorded
    // holiday, or has a paid Leave calendar event (affectsSalary).
    const hasLeaveEvent = cal?.globalEvents?.some(
      ev => (ev.eventType || "").toLowerCase() === "leave" && ev.affectsSalary
    );
    const didWork = workedHrs > 0 || ts.status === "Present" || ts.status === "Holiday"
      || ts.status === "Leave" || hasLeaveEvent;
    // ACTUALLY-worked-this-day flag — distinguishes "showed up and
    // logged hours" from "was on paid leave". Used by the monthly
    // branch to award holiday premium only when the employee really
    // worked on the holiday (not when their paid leave happens to
    // land on one).
    const actuallyWorked = workedHrs > 0 || ts.status === "Present" || ts.status === "Holiday";
    if (didWork) {
      daysWorked += 1;
      if      (type === "regularHoliday") {
        regularHolidayPay += rate * REGULAR_HOLIDAY_MULTIPLIER;
        if (actuallyWorked) regularHolidayWorkedDays += 1;
      }
      else if (type === "specialHoliday") {
        specialHolidayPay += rate * SPECIAL_HOLIDAY_MULTIPLIER;
        if (actuallyWorked) specialHolidayWorkedDays += 1;
      }
      else                                regularPay        += rate; // weekday OR weekend
    }

    if (otHrs    > 0) overtimePay        += otHrs    * hourlyRate * OT_MULTIPLIER;

    // Late / undertime deductions are skipped on day-off rows
    // (Sundays or Holiday-status entries) unless the caller flags the
    // employee / company default as "deduct on day-off too". Matches
    // the Timesheet page's display suppression.
    const isDayOff =
      ts.status === "Holiday" || new Date(year, month, d).getDay() === 0;
    if (applyDeductionsOnDayOff || !isDayOff) {
      if (lateMin  > 0) lateDeduction      += lateMin  * minuteRate;
      if (underMin > 0) undertimeDeduction += underMin * minuteRate;
    }
  }

  // ── Monthly-fixed salary mode ───────────────────────────────────
  // For salaried employees the monthly base is paid in full regardless
  // of how many days were clocked in; only UNPAID absences (status
  // "Absent" rows) prorate it down. The per-day loop above already
  // tracked daysAbsent and the holiday-worked counters; here we
  // rebuild regularPay / regularHolidayPay / specialHolidayPay so the
  // breakdown shows:
  //   - regularPay = full base (= dailyRate × WORK_DAYS_PER_MONTH,
  //     which equals the original monthly salary by construction).
  //   - absentDeduction stays as a separate line so the parent / HR
  //     can see what was deducted (kept identical to daily mode:
  //     daysAbsent × dailyRate).
  //   - holiday premium = ONLY the extra above 1× rate, since the
  //     base is already inside regularPay. Worked regular holiday →
  //     +1.0× rate (so total day pay = base + premium = 2× rate).
  //     Worked special holiday → +0.3× rate.
  //   - overtime, late, undertime, statutory deductions are
  //     unchanged — they're calculated the same in both modes.
  //
  // Anything other than the literal "monthly" string falls through to
  // the existing daily-mode totals so legacy callers and explicitly
  // daily employees keep their current behaviour.
  const isMonthly = String(salaryType || "").toLowerCase() === "monthly";
  if (isMonthly) {
    regularPay        = WORK_DAYS_PER_MONTH * rate;
    regularHolidayPay = regularHolidayWorkedDays * rate * (REGULAR_HOLIDAY_MULTIPLIER - 1);
    specialHolidayPay = specialHolidayWorkedDays * rate * (SPECIAL_HOLIDAY_MULTIPLIER - 1);
  }

  // Per-employee payroll deductions (SSS/PhilHealth/Pag-IBIG/Insurance
  // + any custom "others") — set from the People → Edit Employee form
  // and persisted on Employee.deductionsJson. Flattened here so each
  // line shows up individually in the Salary page breakdown.
  // Extra monthly earnings (incentives, allowances, bonuses, …) set
  // from the People → Edit Employee form. Fully dynamic: each entry is
  // shown as its own line in the breakdown by name.
  const extraEarnings = Array.isArray(earnings)
    ? earnings
        .filter(o => (o?.name || "").trim() !== "")
        .map(o => ({ name: String(o.name).trim(), value: Number(o.value) || 0 }))
    : [];
  const extraEarningsSum = extraEarnings.reduce((s, o) => s + o.value, 0);

  const pd            = payrollDeductions || {};
  const sssDeduction        = Number(pd.sss)        || 0;
  const philhealthDeduction = Number(pd.philhealth) || 0;
  const pagibigDeduction    = Number(pd.pagibig)    || 0;
  const insuranceDeduction  = Number(pd.insurance)  || 0;
  const otherDeductions     = Array.isArray(pd.others)
    ? pd.others
        .filter(o => (o?.name || "").trim() !== "")
        .map(o => ({ name: String(o.name).trim(), value: Number(o.value) || 0 }))
    : [];
  const otherDeductionsSum = otherDeductions.reduce((s, o) => s + o.value, 0);
  const payrollDeductionsTotal =
    sssDeduction + philhealthDeduction + pagibigDeduction + insuranceDeduction + otherDeductionsSum;

  const gross            = regularPay + overtimePay + specialHolidayPay + regularHolidayPay + extraEarningsSum;
  const totalDeductions  = lateDeduction + undertimeDeduction + absentDeduction + payrollDeductionsTotal;
  // Floor net pay at 0. Employees shouldn't "owe" the company —
  // negative net pay happens when fixed statutory deductions
  // (SSS/PhilHealth/Pag-IBIG/Insurance) are applied to an employee
  // with zero or very low gross for the period. Showing a negative
  // peso figure confuses employees and breaks payslip export.
  const netPay           = Math.max(0, gross - totalDeductions);

  return {
    salaryType: isMonthly ? "monthly" : "daily",
    dailyRate: rate,
    hourlyRate,
    daysWorked,
    daysAbsent,
    regularPay,
    overtimePay,
    specialHolidayPay,
    regularHolidayPay,
    extraEarnings,            // [{ name, value }, …]
    extraEarningsSum,
    gross,
    lateDeduction,
    undertimeDeduction,
    absentDeduction,
    sssDeduction,
    philhealthDeduction,
    pagibigDeduction,
    insuranceDeduction,
    otherDeductions,          // [{ name, value }, …]
    otherDeductionsSum,
    payrollDeductionsTotal,
    totalDeductions,
    netPay,
  };
}
